import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useCurrentUser from "hooks/useCurrentUser";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import BranchAdminsCard from "views/tenantAdmin/BranchAdminsCard";
import ChatAgent from "./AgentChat";
import {
  PageContainer, StatCard, PremiumCard, DataTable, Button,
  StatusBadge, LoadingState, ErrorState, EmptyState,
} from "components/ui";

const IconBuilding = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2" />
    <path d="M9 22v-4h6v4" />
  </svg>
);
const IconUsers = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IconCard = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);
const IconCoins = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
    <line x1="12" y1="6" x2="12" y2="8" /><line x1="12" y1="16" x2="12" y2="18" />
  </svg>
);
const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const TenantAdminDashboard = () => {
  const { user, loading: userLoading, error: userError } = useCurrentUser();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const tenant = user?.tenant;

  const [activeSubscription, setActiveSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [allSubLoading, setAllSubLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(true);

  const stats = {
    branches: branches.length,
    users: 5,
    subscriptions: allSubscriptions.length,
    mrr: 149,
  };

  useEffect(() => {
    if (!tenant) { setSubscriptionLoading(false); return; }
    (async () => {
      try {
        const res = await axiosPrivate.get("/api/subscriptions/active");
        setActiveSubscription(res.data || null);
      } catch (err) {
        console.error("Failed to fetch active subscription", err);
      } finally {
        setSubscriptionLoading(false);
      }
    })();
  }, [tenant]);

  useEffect(() => {
    if (!tenant) { setAllSubLoading(false); return; }
    (async () => {
      try {
        const res = await axiosPrivate.get("/api/subscriptions/my");
        setAllSubscriptions(res.data);
      } catch (err) {
        console.error("Failed to fetch subscriptions", err);
      } finally {
        setAllSubLoading(false);
      }
    })();
  }, [tenant]);

  useEffect(() => {
    if (!tenant) { setBranchesLoading(false); return; }
    (async () => {
      try {
        const res = await axiosPrivate.get("/api/branches");
        setBranches(res.data);
      } catch (err) {
        console.error("Failed to fetch branches", err);
      } finally {
        setBranchesLoading(false);
      }
    })();
  }, [tenant]);

  const isTrial = activeSubscription && Number(activeSubscription.pricePaid) === 0;
  const daysLeft = activeSubscription
    ? Math.ceil((new Date(activeSubscription.endDate) - new Date()) / 86400000)
    : 0;

  if (userLoading) {
    return (
      <PageContainer>
        <PremiumCard><LoadingState label="Loading dashboard…" /></PremiumCard>
      </PageContainer>
    );
  }
  if (userError) {
    return (
      <PageContainer>
        <PremiumCard>
          <ErrorState
            title="Unable to load user information"
            action={<Button onClick={() => window.location.reload()}>Retry</Button>}
          />
        </PremiumCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ChatAgent />

      {/* Org card */}
      {tenant ? (
        <PremiumCard
          className="mb-4"
          title={tenant.name}
          subtitle={tenant.slug}
          actions={
            <>
              <StatusBadge value={tenant.active ? "ACTIVE" : "INACTIVE"} dot />
              <Button variant="secondary" size="sm" onClick={() => navigate("/tenant/edit-tenant")}>
                Edit details
              </Button>
              {tenant.active ? (
                <Button variant="primary" size="sm" onClick={() => navigate("/tenant/subscribe")}>
                  Manage subscription
                </Button>
              ) : (
                <Button variant="primary" size="sm" onClick={() => navigate("/home")}>
                  Subscribe
                </Button>
              )}
            </>
          }
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
              gap: 20,
            }}
          >
            <FieldDef label="Contact email" value={tenant.contactEmail} />
            {tenant.contactPhone && <FieldDef label="Phone" value={tenant.contactPhone} />}
            <FieldDef label="Currency" value={tenant.defaultCurrency} />
            <FieldDef label="Language" value={tenant.defaultLanguage} />
            {tenant.address && (
              <div style={{ gridColumn: "1 / -1" }}>
                <FieldDef label="Address" value={tenant.address} />
              </div>
            )}
          </div>
        </PremiumCard>
      ) : (
        <PremiumCard className="mb-4">
          <EmptyState
            title="No organization yet"
            description="Create your organization to unlock plans and team management."
            action={<Button onClick={() => navigate("/tenant/add-tenant")} leftIcon={<IconPlus />}>Create organization</Button>}
          />
        </PremiumCard>
      )}

      {/* Active subscription */}
      {tenant && (
        <PremiumCard className="mb-4" sunken>
          {subscriptionLoading ? (
            <LoadingState />
          ) : activeSubscription ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div>
                <StatusBadge value={isTrial ? "TRIAL" : "ACTIVE"} dot />
                <div className="t-heading-md" style={{ marginTop: 8 }}>{activeSubscription.plan?.name}</div>
                <div className="t-body-sm t-muted" style={{ marginTop: 4 }}>
                  {isTrial
                    ? `Expires in ${daysLeft} day${daysLeft !== 1 ? "s" : ""} · ${new Date(activeSubscription.endDate).toLocaleDateString()}`
                    : `Renews on ${new Date(activeSubscription.renewalDate || activeSubscription.endDate).toLocaleDateString()} · ${activeSubscription.billingCycle === "MONTHLY" ? "Monthly" : "Yearly"}`}
                </div>
              </div>
              <Button variant={isTrial ? "primary" : "secondary"} onClick={() => navigate("/home")}>
                {isTrial ? "Subscribe now" : "Change plan"}
              </Button>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
              <div>
                <div className="t-heading-md">No active subscription</div>
                <div className="t-body-sm t-muted" style={{ marginTop: 4 }}>Pick a plan to unlock all features.</div>
              </div>
              <Button onClick={() => navigate("/home")}>Subscribe now</Button>
            </div>
          )}
        </PremiumCard>
      )}

      {/* KPI strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginBottom: 32,
        }}
      >
        <StatCard label="Branches"      value={stats.branches}      icon={<IconBuilding />} />
        <StatCard label="Users"         value={stats.users}         icon={<IconUsers />} />
        <StatCard label="Subscriptions" value={stats.subscriptions} icon={<IconCard />} />
        <StatCard label="MRR"           value={`${stats.mrr} TND`}  icon={<IconCoins />} />
      </div>

      {/* Two-column: branches + subscriptions */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 16, marginBottom: 24 }}>
        <DataTable
          title="Branches"
          subtitle="Locations under this tenant"
          toolbar={
            <Button variant="primary" size="sm" leftIcon={<IconPlus />} onClick={() => navigate("/tenant/branches/add")}>
              Add branch
            </Button>
          }
          loading={branchesLoading}
          data={branches}
          rowKey={(b) => b.id}
          search={false}
          pageSize={5}
          emptyTitle="No branches yet"
          emptyDescription="Add your first branch to start tracking inventory."
          columns={[
            { key: "name", header: "Name", render: (b) => <strong>{b.name}</strong> },
            { key: "code", header: "Code", render: (b) => <span className="t-mono t-muted">{b.code}</span> },
            { key: "status", header: "Status", render: (b) => <StatusBadge value={b.status} dot /> },
            {
              key: "actions", header: "", align: "right",
              render: (b) => (
                <div style={{ display: "inline-flex", gap: 6 }}>
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/tenant/branches/${b.id}`)}>View</Button>
                  <Button variant="secondary" size="sm" onClick={() => navigate(`/tenant/branches/edit/${b.id}`)}>Edit</Button>
                </div>
              ),
            },
          ]}
        />

        <DataTable
          title="My subscriptions"
          subtitle="Billing history"
          loading={allSubLoading}
          data={allSubscriptions}
          rowKey={(s) => s.id}
          search={false}
          pageSize={5}
          emptyTitle="No subscriptions yet"
          columns={[
            { key: "plan",   header: "Plan",   render: (s) => s.plan?.name || "—" },
            { key: "status", header: "Status", render: (s) => <StatusBadge value={s.status} dot /> },
            { key: "end",    header: "End",    render: (s) => new Date(s.endDate).toLocaleDateString() },
            { key: "price",  header: "Price",  align: "right", render: (s) => <strong>{s.pricePaid} {s.currency}</strong> },
          ]}
        />
      </div>

      {tenant && <BranchAdminsCard />}
    </PageContainer>
  );
};

const FieldDef = ({ label, value }) => (
  <div>
    <div className="t-body-xs">{label}</div>
    <div style={{ color: "var(--text-body)", fontWeight: 500, fontSize: 14, marginTop: 4 }}>{value || "—"}</div>
  </div>
);

export default TenantAdminDashboard;
